import { api } from './api';
import type {
  ClientDetail,
  ClientListItem,
  ClientStatus,
  InviteState,
  PractitionerOption,
} from './types';

/**
 * Clients endpoints and the presentation helpers that go with them.
 *
 * Tone maps live here rather than in types.ts so `lib/` never imports from
 * `design/` — the tone unions are plain strings, mirroring statusTone() in
 * the retired portal mock.
 */

export interface ClientCreateInput {
  name: string;
  email: string;
  goal?: string | null;
  program?: string | null;
  intakeNotes?: string | null;
  practitionerId?: string | null;
}

export type ClientUpdateInput = Partial<{
  name: string;
  goal: string | null;
  program: string | null;
  status: ClientStatus;
  intakeNotes: string | null;
  practitionerId: string | null;
}>;

export const clientsApi = {
  list: () => api.get<{ clients: ClientListItem[] }>('/clients'),
  get: (id: string) => api.get<{ client: ClientDetail }>(`/clients/${id}`),
  create: (body: ClientCreateInput) =>
    api.post<{ client: ClientDetail; inviteSent: boolean }>('/clients', body),
  update: (id: string, body: ClientUpdateInput) =>
    api.patch<{ client: ClientDetail }>(`/clients/${id}`, body),
  resendInvite: (id: string) =>
    api.post<{ client: ClientDetail; inviteSent: boolean }>(`/clients/${id}/invite`),
  practitioners: () => api.get<{ practitioners: PractitionerOption[] }>('/practitioners'),
};

export type Tone = 'success' | 'warning' | 'neutral' | 'danger' | 'brand';

export function clientStatusTone(status: ClientStatus): Tone {
  if (status === 'active') return 'success';
  if (status === 'on_hold') return 'warning';
  return 'neutral';
}

export function inviteTone(state: InviteState): Tone {
  if (state === 'pending') return 'warning';
  if (state === 'expired') return 'danger';
  return 'success';
}

/** No date library in this project, and one isn't worth adding for this. */
export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
